import { fetchGame } from './game';

/**
 * Validates if a string is potentially a valid game ID
 * @param {string} id - The ID to validate
 * @returns {boolean} - True if the ID is in a valid format
 */
export const isValidGameId = (id) => {
  // Check if it matches the UUID format (like the example 3a2e5afe-8070-4de5-b67b-7f28217fb848)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return typeof id === 'string' && uuidPattern.test(id);
};

/**
 * Helper function to get a game by ID with error handling
 * @param {string} id - The game ID to fetch
 * @returns {Promise<Object>} - A promise that resolves to the game data or rejects with an error
 */
export const getGameById = async (id) => {
  if (!isValidGameId(id)) {
    throw new Error('Invalid game ID format');
  }
  
  try {
    const gameData = await fetchGame(id);
    if (!gameData) {
      throw new Error('Game not found');
    }
    return gameData;
  } catch (error) {
    console.error('Error fetching game:', error);
    throw error;
  }
};

/**
 * Get multiple games by their IDs
 * @param {string[]} ids - Array of game IDs to fetch
 * @returns {Promise<Object[]>} - A promise that resolves to an array of game data
 */
export const getGamesByIds = async (ids) => {
  const gamePromises = ids.map(id => 
    getGameById(id)
      .catch(error => {
        console.error(`Failed to fetch game with ID ${id}:`, error);
        return null; // Return null for failed fetches so we still get results for successful ones
      })
  );
  
  const results = await Promise.all(gamePromises);
  return results.filter(game => game !== null); // Filter out any nulls from failed fetches
}; 